"use client";

import { forwardRef, memo, useCallback, useContext, useMemo, useState } from "react";
import { GroupedTableVirtuoso, type ItemProps, type ScrollerProps, type TableProps } from "react-virtuoso";
import { VirtuosoErrorBoundary } from "@/components/virtuoso-error-boundary";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { PlayerAvatar, TeamLogo } from "@/components/ui/avatar";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { NOT_BETTABLE_CLASS, notBettableIn, notHere } from "@/lib/book-location";
import { useGameLadder, useLadderPrefetch, useRow } from "@/hooks/use-odds-live";
import { ladderLineSummaries } from "@/lib/odds-screen/live/derive";
import { ladderEventOf } from "@/lib/odds-screen/live/inplay";
import type { EventOpeners } from "@/lib/multibook/fetch";
import type { BookId, EventHeader, EventId, FamilyLayout, FamilySummary, LeagueId, MarketSpec, OddsDisplay, RowId } from "@/lib/odds-screen/live/types";
import { allBooksRow, ladderQuote, pinLine, type AllBooksCell } from "./all-books-model";
import { LinePicker } from "./line-picker";
import { OPENER_BOOK, hasOpeners, openPrices, useOpeners } from "./openers";
import { marketDisplayOrder } from "./view-model";
import { drawName, formatStake, matchupLabel, matchupSides, outcomeName, outcomeSideLabel, rowLines, teamLogoUrl, type MatchupSide } from "./labels";
import { BetChip, BookStakeContext, StakeText, stakeLabel } from "./price-cell";
import { betPick, type BetActions } from "./bet";
import { LatestPlay, LiveState, SideScore, sideTone } from "./live-state";
import { SubjectCell } from "./subject-cell";
import { rowSpec, runsOf, subjectOf } from "./subject";
import { KICKOFF_TIME, LIVE_START, groupStart, kickoffGroups, kickoffKeys, kickoffParts, metaDate } from "./time";
import { MinStakeContext, belowMinText } from "./use-min-stake";

export type AllBooksTarget = { readonly slot: number; readonly line: number | null; readonly outcome: number };

type AllBooksRowData = { readonly rowId: RowId; readonly event: EventHeader | undefined; readonly entityName: string | null };

type AllBooksContext = {
  readonly league: LeagueId;
  readonly family: FamilySummary;
  readonly slot: number;
  readonly spec: MarketSpec;
  readonly rows: readonly AllBooksRowData[];
  readonly marketOrder: readonly number[];
  readonly onSelectMarket: (slot: number) => void;
  readonly books: readonly BookId[];
  readonly selected: ReadonlySet<BookId>;
  readonly shown: ReadonlySet<BookId>;
  readonly format: OddsDisplay;
  readonly drill: { readonly rowId: RowId; readonly outcome: number } | null;
  readonly onOpen: (rowId: RowId, target: AllBooksTarget) => void;
  readonly onPickLine: (key: string, line: number | null) => void;
  readonly bet: BetActions;
  readonly consensus: boolean;
  readonly openers: ReadonlyMap<EventId, EventOpeners> | null;
  readonly totalWidth: number;
};

export function pickKey(familyId: string, slot: number, rowId: RowId): string {
  return `${familyId}:${slot}:${rowId}`;
}

const BEST_WIDTH = 128;
const BOOK_WIDTH = 76;
const PINNED_SHADOW = "shadow-[1px_0_0_rgba(255,255,255,0.08)]";
const CONSENSUS_TITLE = "The no-vig price of each side, from the consensus of the books. Not a bettable price.";
const OPEN_TITLE = "Pinnacle's first price for this market, with its line when the line has moved since.";
const NO_OPEN_TITLE = "No Pinnacle opener";

function leftColumns(game: number, line: number | null) {
  return {
    total: game + (line ?? 0) + BEST_WIDTH,
    gameStyle: { width: game },
    lineStyle: line === null ? null : { left: game, width: line },
    bestStyle: { left: game + (line ?? 0), width: BEST_WIDTH },
    consensusStyle: { left: game + (line ?? 0) + BEST_WIDTH, width: BOOK_WIDTH },
    openStyle: (consensus: boolean) => ({ left: game + (line ?? 0) + BEST_WIDTH + (consensus ? BOOK_WIDTH : 0), width: BOOK_WIDTH }),
  } as const;
}

const STICKY_LEFT_COLUMNS: Readonly<Record<FamilyLayout, ReturnType<typeof leftColumns>>> = { sides: leftColumns(248, 96), named: leftColumns(240, 132), results: leftColumns(280, null) };

const BOOK_STYLE = { width: BOOK_WIDTH } as const;

const Scroller = forwardRef<HTMLDivElement, ScrollerProps & { context?: AllBooksContext }>(function Scroller(
  { context: _context, style, ...props },
  ref,
) {
  return <div ref={ref} {...props} className="scrollbar-custom scrollbar-color-primary" style={{ ...style, height: "100%", overflow: "auto" }} />;
});

function TableEl({ context, style, ...props }: TableProps & { context?: AllBooksContext }) {
  return <table {...props} style={{ ...style, width: context?.totalWidth, tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }} />;
}

function TableRowEl({ item: _item, context, ...props }: ItemProps<AllBooksRowData> & { context?: AllBooksContext }) {
  const row = context?.rows[props["data-item-index"]];
  const rowSelected = context?.drill?.rowId === row?.rowId;
  const liveSides = context?.family.layout === "sides" && row?.event?.live !== undefined;
  return <tr {...props} className={cn(context?.family.layout === "results" ? "h-11" : "h-16", liveSides && "align-top [&>td]:py-[5px]", rowSelected && "bg-primary/[0.03]")} />;
}

const EMPTY_OUTCOMES: readonly number[] = [];
const NONE_CELL: AllBooksCell = { kind: "none" };

function sameCell(a: AllBooksCell, b: AllBooksCell): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "main" && b.kind === "main") return a.display === b.display && a.stake === b.stake && a.belowMin === b.belowMin && a.best === b.best && a.held === b.held;
  if (a.kind === "off" && b.kind === "off") return a.display === b.display && a.stake === b.stake && a.belowMin === b.belowMin && a.off === b.off && a.held === b.held;
  return true;
}

type BookColumnProps = {
  book: BookId;
  cells: readonly AllBooksCell[];
  outcomes: readonly number[];
  rowId: RowId;
  slot: number;
  line: number | null;
  onOpen: (rowId: RowId, target: AllBooksTarget) => void;
};

const BookColumn = memo(function BookColumn({ book, cells, outcomes, rowId, slot, line, onOpen }: BookColumnProps) {
  const kind = useContext(BookStakeContext).get(book) ?? null;
  const { isHere, place } = useBookAvailability();
  const away = notHere(place, isHere(book));
  const awayLabel = (label: string) => (away ? notBettableIn(label, away) : label);
  return (
    <td className="border-b border-white/[0.06]" style={BOOK_STYLE}>
      <div className={cn("flex flex-col justify-center gap-0.5 px-[3px]", away && NOT_BETTABLE_CLASS)}>
        {cells.map((cell, index) => (
          <button
            key={index}
            type="button"
            disabled={cell.kind === "none"}
            onClick={cell.kind === "none" ? undefined : () => onOpen(rowId, { slot, line, outcome: outcomes[index] })}
            aria-label={cell.kind === "none" ? `${sportsbookIdToDisplay(book)} not offered` : awayLabel(`${sportsbookIdToDisplay(book)} ${cell.kind === "off" ? `${cell.off} ` : ""}${cell.display}${cell.held ? ", paused" : stakeLabel(formatStake(cell.stake, kind))}${cell.belowMin ? ", below your minimum" : ""}`)}
            className={cn(
              "data-num flex h-[26px] w-full items-center justify-center gap-1 rounded-md text-[13px] font-medium",
              cell.kind === "none" && "cursor-default text-foreground/20",
              cell.kind === "off" && (cell.held ? "text-foreground/30 hover:bg-white/[0.06]" : "text-zinc-500 hover:bg-white/[0.06]"),
              cell.kind === "main" && (cell.held ? "text-foreground/30 hover:bg-white/[0.06]" : cell.best ? "bg-accent-value/[0.06] font-semibold text-accent-value" : "text-foreground/80 hover:bg-white/[0.06]"),
              cell.kind !== "none" && cell.belowMin && "opacity-40",
            )}
          >
            {cell.kind === "off" && <span className="text-[10px] text-zinc-600">{cell.off}</span>}
            {cell.kind !== "none" && (
              <span className="flex flex-col items-center">
                <span className="leading-[14px]">{cell.display}</span>
                <StakeText stake={cell.held ? null : formatStake(cell.stake, kind)} className="font-normal leading-[10px]" />
              </span>
            )}
          </button>
        ))}
      </div>
    </td>
  );
}, (a, b) =>
  a.book === b.book && a.rowId === b.rowId && a.slot === b.slot && a.line === b.line && a.onOpen === b.onOpen
  && a.outcomes.length === b.outcomes.length && a.outcomes.every((value, i) => value === b.outcomes[i])
  && a.cells.length === b.cells.length && a.cells.every((cell, i) => sameCell(cell, b.cells[i])),
);

const AllBooksCells = memo(function AllBooksCells({
  rowId, event, entityName, pick, context,
}: {
  rowId: RowId;
  event: EventHeader | undefined;
  entityName: string | null;
  pick: number | null;
  context: AllBooksContext;
}) {
  const { league, family, slot, spec, books, selected, shown, format, drill, onOpen, onPickLine, bet, consensus, openers } = context;
  const kinds = useContext(BookStakeContext);
  const minStake = useContext(MinStakeContext);
  const boardRow = useRow(league, family.id, rowId, event, family.markets);
  const prefetch = useLadderPrefetch();
  const [open, setOpen] = useState(false);
  const { gameStyle, lineStyle, bestStyle, consensusStyle, openStyle } = STICKY_LEFT_COLUMNS[family.layout];
  const ladderTarget = lineStyle !== null && spec.line !== "none" && event !== undefined && boardRow !== undefined
    ? { league, event: event.id, family: family.id, entity: boardRow.entity?.key ?? "" }
    : null;
  const ladder = useGameLadder(pick !== null || open ? ladderTarget : null, event, family.markets);
  const pinned = pick === null ? null : (ladder && ladderQuote(ladder, slot, pick)) ?? { main: pick, lines: 0, books: [] };
  const quote = pinned ?? boardRow?.markets[slot] ?? null;
  const results = family.layout === "results";
  const cellSpec = results ? rowSpec(family, boardRow) : spec;
  const model = quote ? allBooksRow(quote, cellSpec, event, family.layout, books, selected, format, minStake) : null;
  const topic = { kind: "ladder", league, event: ladderEventOf(event), family: family.id, entity: boardRow?.entity?.key ?? "" } as const;
  const rowSelected = drill?.rowId === rowId;
  const drillOutcome = rowSelected ? drill?.outcome ?? null : null;

  const named = family.layout === "named";
  const lines = rowLines([spec], entityName !== null);
  const slots = Array.from({ length: named || results ? spec.outcomes.length : lines.length });
  const teams: readonly MatchupSide[] = entityName === null
    ? matchupSides(event, lines, drawName([spec]))
    : [{ line: "away", name: entityName, logo: null, live: null }, { line: "home", name: matchupLabel(event, league), logo: null, live: null }];
  const rowLabels = model === null ? slots.map(() => "") : model.labels;
  const opens = openers !== null && model !== null && quote !== null && event !== undefined ? openPrices(openers.get(event.id), cellSpec, quote.main, model.outcomes, format) : null;

  return (
    <>
      <td className="sticky left-0 z-[1] border-b border-white/[0.06] bg-surface-raised" style={gameStyle}>
        {named || results ? (
          <SubjectCell subject={subjectOf(family, boardRow, event, league)} className="px-4" />
        ) : (
          <div className="flex min-w-0 items-center gap-2.5 px-4">
            {entityName !== null && (family.kind === "props"
              ? <PlayerAvatar name={entityName} imageUrl={boardRow?.player?.imageUrl ?? null} size={32} />
              : <TeamLogo name={entityName} imageUrl={teamLogoUrl(event, entityName)} size={32} />)}
            <div className="flex min-w-0 flex-grow flex-col gap-0.5">
              {teams.map(({ line, name, logo, live }, i) => (
                <span key={line} className={cn("flex h-[26px] min-w-0 items-center gap-2 text-[13px] font-medium leading-5", live ? sideTone(live) : i === 0 ? "text-foreground" : "text-zinc-400")}>
                  {entityName === null && event && line !== "draw" && <TeamLogo name={name} imageUrl={logo} size={20} />}
                  <span className={cn("truncate", line === "draw" && "pl-7")}>{name}</span>
                  <SideScore live={live} inlinePeriods={false} />
                </span>
              ))}
              {entityName === null && event?.live && (
                <>
                  <LiveState live={event.live} compact className="ml-7 self-start" />
                  <LatestPlay play={event.live.plays[0] ?? null} className="ml-7" />
                </>
              )}
            </div>
          </div>
        )}
      </td>
      {lineStyle !== null && (
        <td className="sticky z-[1] border-b border-white/[0.06] bg-surface-raised" style={lineStyle}>
          {ladderTarget !== null && model !== null ? (
            <div className="px-1.5" onPointerEnter={() => { if (event?.live === undefined) prefetch(ladderTarget); }}>
              <LinePicker
                spec={spec}
                labels={rowLabels}
                pinned={pick !== null}
                open={open}
                summaries={ladder === null ? null : ladderLineSummaries(ladder, slot, shown)}
                selectedLine={pick ?? ladder?.mains[slot] ?? null}
                onOpenChange={setOpen}
                onPick={(line) => onPickLine(pickKey(family.id, slot, rowId), line)}
              />
            </div>
          ) : (
            <div className="flex flex-col justify-center gap-0.5 px-3">
              {rowLabels.map((label, i) => (
                <span key={i} title={named ? label : undefined} className="data-num flex h-[26px] items-center text-[12px] text-zinc-400">
                  {named ? <span className="truncate">{label}</span> : label}
                </span>
              ))}
            </div>
          )}
        </td>
      )}
      <td className={cn("sticky z-[1] border-b border-white/[0.06] bg-surface-raised", !consensus && openers === null && PINNED_SHADOW)} style={bestStyle}>
        <div className="flex flex-col justify-center gap-0.5 px-2">
          {(model?.best ?? slots.map(() => null)).map((best, index) => {
            if (model !== null && quote !== null && model.bestBelowMin[index]) {
              const outcome = model.outcomes[index];
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => onOpen(rowId, { slot, line: pick, outcome })}
                  aria-label={`${outcomeSideLabel(cellSpec, quote.main, event, outcome)}, ${belowMinText(minStake).toLowerCase()}. Compare all books`}
                  title={belowMinText(minStake)}
                  className={cn("flex h-[26px] w-full items-center rounded-md px-2 text-[13px] text-zinc-600 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40", rowSelected && drillOutcome === outcome && "bg-primary/10 ring-1 ring-primary/55")}
                >
                  &mdash;
                </button>
              );
            }
            if (best === null || model === null || quote === null) {
              return <button key={index} type="button" disabled aria-label="No price" className="flex h-[26px] cursor-default items-center rounded-md px-2" />;
            }
            const outcome = model.outcomes[index];
            const choice = betPick(best.books, bet.hasAccount);
            const stake = best.held ? null : formatStake(best.stakes.get(choice.book), kinds.get(choice.book) ?? null);
            return (
              <div
                key={index}
                className={cn("grid h-[26px] items-stretch overflow-hidden rounded-md", rowSelected && drillOutcome === outcome && "bg-primary/10 ring-1 ring-primary/55")}
                style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}
              >
                <button
                  type="button"
                  onClick={() => onOpen(rowId, { slot, line: pick, outcome })}
                  aria-label={`${results && entityName !== null ? `${entityName}: ` : ""}${best.held ? "Paused at" : "Best"} ${best.display} at ${sportsbookIdToDisplay(choice.book)}${stakeLabel(stake)}. Compare all books`}
                  className="flex min-w-0 items-center px-2 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                >
                  <span className="flex flex-col items-start">
                    <span className={cn("data-num text-[13px] font-semibold leading-[14px]", best.held ? "text-zinc-500" : "text-foreground")}>{best.display}</span>
                    <StakeText stake={stake} className="leading-[10px]" />
                  </span>
                </button>
                <BetChip
                  books={best.books}
                  pick={choice}
                  subject={entityName ?? matchupLabel(event, league, "spoken")}
                  what={`${outcomeSideLabel(cellSpec, quote.main, event, outcome)} ${best.display}`}
                  logoSize={14}
                  ringColor="#16171a"
                  max={1}
                  held={best.held === true}
                  className="px-1.5 hover:bg-white/[0.06]"
                  onBet={() => topic.event === "" ? onOpen(rowId, { slot, line: pick, outcome }) : bet.open({ topic, spec: cellSpec, slot, line: quote.main, outcome, book: choice.book, decimal: best.decimal }, () => onOpen(rowId, { slot, line: pick, outcome }))}
                  onPrefetch={() => { if (topic.event !== "") prefetch(topic); }}
                />
              </div>
            );
          })}
        </div>
      </td>
      {consensus && (
        <td className={cn("sticky z-[1] border-b border-white/[0.06] bg-surface-raised", openers === null && PINNED_SHADOW)} style={consensusStyle}>
          <div className="flex flex-col justify-center gap-0.5 px-[3px]">
            {model?.consensus?.map((price, index) => (
              <span key={index} className="data-num flex h-[26px] items-center justify-center text-[13px] font-medium text-zinc-400">{price}</span>
            ))}
          </div>
        </td>
      )}
      {openers !== null && (
        <td className={cn("sticky z-[1] border-b border-white/[0.06] bg-surface-raised", PINNED_SHADOW)} style={openStyle(consensus)}>
          <div className="flex flex-col justify-center gap-0.5 px-[3px]">
            {model !== null && opens?.map((price, index) => price === "none" ? (
              <span key={index} role="img" aria-label={`${outcomeName(cellSpec, event, model.outcomes[index])}, no Pinnacle opener`} title={NO_OPEN_TITLE} className="data-num flex h-[26px] items-center justify-center text-[13px] font-medium text-zinc-500">—</span>
            ) : (
              <span key={index} className="data-num flex h-[26px] items-center justify-center gap-1 text-[13px] font-medium text-zinc-400">
                {price?.line && <span className="text-[10px] text-zinc-600">{price.line}</span>}
                {price?.display}
              </span>
            ))}
          </div>
        </td>
      )}
      {(model?.columns ?? books.map((book) => ({ book, cells: slots.map(() => NONE_CELL) }))).map((column) => (
        <BookColumn
          key={column.book}
          book={column.book}
          cells={column.cells}
          outcomes={model?.outcomes ?? EMPTY_OUTCOMES}
          rowId={rowId}
          slot={slot}
          line={pick}
          onOpen={onOpen}
        />
      ))}
    </>
  );
});

function HeaderRow({ context }: { context: AllBooksContext }) {
  const { family, spec, marketOrder, slot, onSelectMarket, books, consensus, openers } = context;
  const { isHere, place } = useBookAvailability();
  const specs = family.markets;
  const { gameStyle, lineStyle, bestStyle, consensusStyle, openStyle } = STICKY_LEFT_COLUMNS[family.layout];
  return (
    <tr className="h-12">
      <th className="sticky left-0 z-[2] border-b border-surface-line bg-surface-chrome text-left align-middle" style={gameStyle}>
        <div className="flex items-center px-4">
          {family.kind === "lines" && specs.length > 1 ? (
            <div role="group" aria-label="Market" className="flex h-[30px] items-center gap-0.5 rounded-lg bg-white/[0.04] p-0.5">
              {marketOrder.map((s) => (
                <button
                  key={specs[s].id}
                  type="button"
                  aria-pressed={s === slot}
                  onClick={() => onSelectMarket(s)}
                  className={cn("h-6 rounded-md px-2.5 text-[12px] font-medium", s === slot ? "bg-white/[0.10] text-foreground" : "text-zinc-400 hover:text-foreground")}
                >
                  {specs[s].label}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">{spec.label}</span>
          )}
        </div>
      </th>
      {lineStyle !== null && (
        <th className="sticky z-[2] border-b border-surface-line bg-surface-chrome text-left align-middle" style={lineStyle}>
          <div className="flex items-center px-3 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">{family.layout === "named" ? null : "Line"}</div>
        </th>
      )}
      <th className={cn("sticky z-[2] border-b border-surface-line bg-surface-chrome text-left align-middle", !consensus && openers === null && PINNED_SHADOW)} style={bestStyle}>
        <div className="flex items-center px-3 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Best</div>
      </th>
      {consensus && (
        <th title={CONSENSUS_TITLE} className={cn("sticky z-[2] border-b border-surface-line bg-surface-chrome align-middle", openers === null && PINNED_SHADOW)} style={consensusStyle}>
          <div className="flex items-center justify-center px-1 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Consensus</div>
        </th>
      )}
      {openers !== null && (
        <th title={OPEN_TITLE} className={cn("sticky z-[2] border-b border-surface-line bg-surface-chrome align-middle", PINNED_SHADOW)} style={openStyle(consensus)}>
          <div className="flex items-center justify-center gap-1 px-1 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">
            <SportsbookLogoMark sportsbook={OPENER_BOOK} size={14} decorative />
            Open
          </div>
        </th>
      )}
      {books.map((book) => {
        const away = notHere(place, isHere(book));
        return (
          <th key={book} title={away ? notBettableIn(sportsbookIdToDisplay(book), away) : sportsbookIdToDisplay(book)} className="border-b border-surface-line bg-surface-chrome align-middle" style={BOOK_STYLE}>
            <div className={cn("flex min-w-0 flex-col items-center justify-center gap-1 px-1", away && NOT_BETTABLE_CLASS)}>
              <SportsbookLogoMark sportsbook={book} size={18} decorative />
              <span className="max-w-[68px] truncate text-[10px] font-medium text-zinc-500">{sportsbookIdToDisplay(book)}</span>
              {away && <span className="whitespace-nowrap text-[10px] font-medium text-zinc-500">Not in {away}</span>}
            </div>
          </th>
        );
      })}
    </tr>
  );
}

export function AllBooksBoard({
  league, family, marketSlot, onSelectMarket, rows, books, selected, format, showDate, drill, bet, onOpen,
}: {
  league: LeagueId;
  family: FamilySummary;
  marketSlot: number;
  onSelectMarket: (slot: number) => void;
  rows: readonly AllBooksRowData[];
  books: readonly BookId[];
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  showDate: boolean;
  drill: { readonly rowId: RowId; readonly outcome: number } | null;
  bet: BetActions;
  onOpen: (rowId: RowId, target: AllBooksTarget) => void;
}) {
  const specs = family.markets;
  const slot = specs[marketSlot] ? marketSlot : 0;
  const spec = specs[slot] ?? specs[0];
  const marketOrder = useMemo(() => marketDisplayOrder(specs), [specs]);
  const consensus = spec.outcomes.length === 2;
  const [picks, setPicks] = useState<ReadonlyMap<string, number>>(() => new Map());
  const onPickLine = useCallback((key: string, line: number | null) => setPicks((current) => pinLine(current, key, line)), []);
  const shown = useMemo(() => new Set(books), [books]);
  const opensShown = hasOpeners(family.id);
  const loadedOpeners = useOpeners(league, opensShown ? rows.flatMap((row) => (row.event ? [row.event.id] : [])) : []);
  const openers = opensShown ? loadedOpeners : null;
  const context = useMemo<AllBooksContext>(() => ({
    league, family, slot, spec, rows, marketOrder, onSelectMarket,
    books, selected, shown, format, drill, onOpen, onPickLine, bet, consensus, openers,
    totalWidth: STICKY_LEFT_COLUMNS[family.layout].total + ((consensus ? 1 : 0) + (openers === null ? 0 : 1) + books.length) * BOOK_WIDTH,
  }), [league, family, slot, spec, rows, marketOrder, onSelectMarket, books, selected, shown, format, drill, onOpen, onPickLine, bet, consensus, openers]);
  const components = useMemo(() => ({ Scroller, Table: TableEl, TableRow: TableRowEl }), []);
  const results = family.layout === "results";
  const runs = useMemo(() => (results ? runsOf(rows, (row) => row.event?.id ?? "") : null), [results, rows]);
  const groups = useMemo(
    () => (runs === null ? kickoffGroups(rows.map((row) => groupStart(row.event) ?? 0)) : { counts: runs.map((run) => run.length), starts: runs.map(([first]) => first.event?.startMs ?? 0) }),
    [runs, rows],
  );
  const keys = useMemo(() => kickoffKeys(groups, rows.map((row) => row.rowId)), [groups, rows]);

  return (
    <VirtuosoErrorBoundary>
      <GroupedTableVirtuoso
        className="min-h-0 flex-grow"
        style={{ height: "100%", width: "100%" }}
        groupCounts={groups.counts}
        context={context}
        components={components}
        computeItemKey={(index) => keys[index] ?? index}
        fixedHeaderContent={() => <HeaderRow context={context} />}
        groupContent={(groupIndex) => {
          const startMs = groups.starts[groupIndex];
          const live = startMs === LIVE_START;
          const parts = startMs && !live ? kickoffParts(startMs) : null;
          const event = runs?.[groupIndex][0].event;
          return (
            <td colSpan={(results ? 2 : 3) + (consensus ? 1 : 0) + (openers === null ? 0 : 1) + books.length} className="border-t border-b border-t-white/[0.12] border-b-surface-line bg-surface-overlay p-0">
              <div className="sticky left-0 flex h-[34px] w-fit items-center gap-[10px] px-4">
                {event ? (
                  <>
                    <span className="whitespace-nowrap text-[12px] font-semibold text-foreground">{matchupLabel(event, league, "full")}</span>
                    {showDate && <span className="whitespace-nowrap text-[12px] text-zinc-400">{metaDate(event.startMs)}</span>}
                  </>
                ) : (parts === null || showDate) && <span className="whitespace-nowrap text-[12px] font-semibold text-foreground">{live ? "Live" : parts ? parts.date : "Scheduled"}</span>}
                {parts && <span className={KICKOFF_TIME}>{parts.time}</span>}
              </div>
            </td>
          );
        }}
        itemContent={(index) => {
          const row = rows[index];
          return <AllBooksCells rowId={row.rowId} event={row.event} entityName={row.entityName} pick={picks.get(pickKey(family.id, slot, row.rowId)) ?? null} context={context} />;
        }}
      />
    </VirtuosoErrorBoundary>
  );
}
